import React from 'react'
import * as Yup from 'yup'
import { get, keyBy } from 'lodash'
import PropTypes from 'prop-types'
import { Field, Formik, Form } from 'formik'
import styled from 'styled-components'
import { withAWSAuth } from '../src/hoc'
import AuthWrapper, { RequestErrorWrapper } from '../src/components/layouts/Auth/Auth.wrapper'
import { Icon, FlexContainer, Text, Row, Column, FormikInput, Button } from '../src/components/common'

const ValidationSchema = Yup.object().shape({
    username: Yup.string()
        .email('form.validations.email')
        .required('form.validations.required'),
    code: Yup.string()
        .required('form.validations.required'),
    password: Yup.string()
        .min(8, 'form.validations.min_length')
        .max(20, 'form.validations.max_length')
        .matches(/\d/, 'form.validations.one_number')
        .matches(/[A-Z]/, 'form.validations.one_upper_caps')
        .required('form.validations.password_required'),
    passwordConfirm: Yup.string()
        .oneOf([Yup.ref('password'), null], 'form.validations.password_match')
        .required('form.validations.password_required')
})

const PASSWORD_VALIDATIONS = keyBy([
    { key: 'form.validations.min_length', validation: (value) => String(value).length >= 8 },
    { key: 'form.validations.max_length', validation: (value) => String(value).length <= 20 },
    { key: 'form.validations.one_number', validation: (value) => new RegExp(/\d/).test(value) },
    { key: 'form.validations.one_upper_caps', validation: (value) => new RegExp(/[A-Z]/).test(value) },
    { key: 'form.validations.password_required', validation: (value) => !['undefined', 'null'].includes(typeof value) }
], 'key')

const renderChecked = (key, value) => {
    if (value.length <= 0) return null
    return get(PASSWORD_VALIDATIONS, key).validation(value) ? <Icon name='check' color='#4cb358' right={5} /> : <Icon name='cross' color='#db5959' right={5} />
}

const validationTextColor = (key, value) => {
    if (value.length <= 0) return '#333333'
    return get(PASSWORD_VALIDATIONS, key).validation(value) ? '#4cb358' : '#db5959'
}

const PasswordValidation = ({ value }) => {
    return (
        <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
            <li>{renderChecked('form.validations.min_length', value)}<Text color={validationTextColor('form.validations.min_length', value)} text='form.validations.min_length' size={12} /></li>
            <li>{renderChecked('form.validations.one_number', value)} <Text color={validationTextColor('form.validations.one_number', value)} text='form.validations.one_number' size={12} /></li>
            <li>{renderChecked('form.validations.one_upper_caps', value)} <Text color={validationTextColor('form.validations.one_upper_caps', value)} text='form.validations.one_upper_caps' size={12} /></li>
        </ul>
    )
}

const FormContainer = styled(FlexContainer)``

const UpdatePasswordDisplayForm = ({ errors, dirty, isSubmitting, handleSubmit, handleChange, handleReset, submitCount, values, isValid, ...props }) => (
    (
        <Form onSubmit={handleSubmit} onReset={handleReset} autoComplete='off'>
            <FormContainer direction='column'>
                <Row>
                    <Column justify='flex-end'>
                        <Text text='form.update_password.title' style={{ fontSize: '20px', color: '#333333', letterSpacing: '1px' }} />
                        <Text text='form.update_password.subtitle' style={{ fontSize: '12px', margin: '10px 0px' }} strong />
                    </Column>
                </Row>
                <Row>
                    <Column justify='flex-end'>
                        <PasswordValidation value={get(values, 'password', '')} />
                    </Column>
                </Row>
                <Row>
                    <Column>
                        <Field
                            style={{ padding: 0, margin: '7px 0' }}
                            component={FormikInput}
                            name='password'
                            type='password'
                            label='Nueva clave'
                            hasFeedback
                            placeholder='*******'
                            disabled={isSubmitting}
                            allowClear
                            submitCount={submitCount}
                        />
                    </Column>
                </Row>
                <Row>
                    <Column>
                        <Field
                            style={{ padding: 0, margin: '7px 0' }}
                            component={FormikInput}
                            name='passwordConfirm'
                            type='password'
                            label='Reingresar nueva clave'
                            hasFeedback
                            placeholder='*******'
                            disabled={isSubmitting}
                            allowClear
                            submitCount={submitCount}
                        />
                    </Column>
                </Row>
                <Row>
                    <Column>
                        <Field
                            style={{ padding: 0 }}
                            component={FormikInput}
                            name='code'
                            type='input'
                            label='Ingresa el código enviado a tu correo'
                            hasFeedback
                            placeholder=''
                            disabled={isSubmitting}
                            allowClear
                            submitCount={submitCount}
                        />
                    </Column>
                </Row>
                {errors && errors['response'] && <Row>
                    <Column>
                        <RequestErrorWrapper>
                            <Text text={errors['response']} />
                        </RequestErrorWrapper>
                    </Column>
                </Row>}
                <Row style={{ margin: '15px 0px' }}>
                    <Column>
                        <Button type='submit' className='login-form-button' disabled={isSubmitting || !isValid} loading={isSubmitting} text='form.update_password.btn.submit' />
                    </Column>
                </Row>
            </FormContainer >
        </Form>
    )
)

const UpdatePasswordRequestForm = ({ handleSubmit, handleReset, initialValues, isSubmitting }) => (
    <Formik
        onSubmit={handleSubmit}
        onReset={handleReset}
        initialValues={initialValues}
        validationSchema={ValidationSchema}
        render={UpdatePasswordDisplayForm}
    />
)

class UpdatePasswordRequest extends React.Component {
    static async getInitialProps ({ query }) {
        const currentUsername = get(query, 'u')
        return { currentUsername }
    }

    handleSubmit = (values, { setSubmitting, setFieldError }) => {
        const { awsAuth } = this.props
        setSubmitting(true)
        awsAuth.forgotPasswordSubmit(values)
            .then(() => Promise.resolve(window.localStorage.removeItem('rp_auth_username')))
            .then((resp) => this.props.history.push('/'))
            .catch((err) => {
                const errorCode = get(err, 'code')
                setFieldError('response', `form.auth.error.${errorCode}`)
                setSubmitting(false)
            })
    };

    render () {
        const { currentUsername } = this.props
        const username = window.localStorage.getItem('rp_auth_username') || currentUsername

        return (

            <AuthWrapper >
                <UpdatePasswordRequestForm handleSubmit={this.handleSubmit.bind(this)} initialValues={{ code: '', password: '', passwordConfirm: '', username }} />
            </AuthWrapper>
        )
    }
}

UpdatePasswordRequest.propTypes = {
    currentUsername: PropTypes.any
}

export default withAWSAuth(UpdatePasswordRequest);
